import {
  PlatformMemberChangeResult,
  PlatformPagedResult,
  PlatformSubscriptionDetail,
  PlatformSubscriptionRow,
  PlatformTeamDetail,
  PlatformTeamRole,
  PlatformTeamRow,
  PlatformTeamUserRow,
  PlatformUserDetail,
} from "../../common/platformTypes";
import { post } from "./rest";

/**
 * Platform teams and platform-only users, proxied through the admin server.
 *
 * `/platform-teams` rather than `/teams`: that path is already the Slack
 * channels controller.
 */

/**
 * `rest.post` only throws for 401/400/404, so a 500 resolves with the server's
 * `{error, message}` body instead. Reading `.items` off that yields undefined
 * and the screen crashes on the next `.map` rather than saying what broke.
 */
async function platformPost<T>(path: string, params?: unknown): Promise<T> {
  const data = await post(path, params);

  if (data && typeof data === "object" && (data as { error?: unknown }).error) {
    const { error, message } = data as { error: string; message?: string };
    throw new Error(message || error);
  }

  return data as T;
}

// ── Teams ────────────────────────────────────────────────────────────

export async function listTeams(params: {
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<PlatformPagedResult<PlatformTeamRow>> {
  return await platformPost("/platform-teams/list", params);
}

export async function teamInfo(teamId: string): Promise<PlatformTeamDetail> {
  return await platformPost("/platform-teams/info", { teamId });
}

/**
 * Team-scoped, unlike `subscriptionMemberAdd` — a team with no subscription
 * still needs members added.
 */
export async function teamMemberAdd(params: {
  teamId: string;
  /** Platform user id or email. */
  identifier: string;
  role: PlatformTeamRole;
  /** Set on the retry after the operator confirms going over quota. */
  acknowledgeSeatChange?: boolean;
}): Promise<PlatformMemberChangeResult> {
  return await platformPost("/platform-teams/member/add", params);
}

export async function teamMemberRemove(params: {
  teamId: string;
  userId: string;
}): Promise<PlatformMemberChangeResult> {
  return await platformPost("/platform-teams/member/remove", params);
}

export async function teamMemberRole(params: {
  teamId: string;
  userId: string;
  role: PlatformTeamRole;
  /** Set on the retry after the operator confirms going over quota. */
  acknowledgeSeatChange?: boolean;
}): Promise<PlatformMemberChangeResult> {
  return await platformPost("/platform-teams/member/role", params);
}

// ── Platform-only users ──────────────────────────────────────────────

export async function searchPlatformUsers(
  search: string,
): Promise<PlatformTeamUserRow[]> {
  return await platformPost("/platform-users/search", { search });
}

export async function platformUserInfo(
  userId: string,
): Promise<PlatformUserDetail> {
  return await platformPost("/platform-users/info", { userId });
}

export async function platformUserSubscriptions(
  userId: string,
): Promise<PlatformSubscriptionRow[]> {
  return await platformPost("/platform-users/subscriptions", { userId });
}

// ── Subscriptions ────────────────────────────────────────────────────

export async function subscriptionInfo(
  subscriptionId: string,
): Promise<PlatformSubscriptionDetail> {
  return await platformPost("/platform-subscriptions/info", { subscriptionId });
}

export type PlatformSubscriptionUpdate = {
  subscriptionId: string;
  tier?: string;
  licenses?: number;
  endDate?: string;
  clearEndDate?: boolean;
  isTrial?: boolean;
  isOneTime?: boolean;
  featureOverrides?: Record<string, boolean | number>;
  billingAdminIds?: string[];
  memo?: string;
  stripeCustomerId?: string;
  stripeSubscriptionItemId?: string;
};

export async function subscriptionUpdate(
  params: PlatformSubscriptionUpdate,
): Promise<PlatformSubscriptionRow> {
  return await platformPost("/platform-subscriptions/update", params);
}

export async function subscriptionSetActive(params: {
  subscriptionId: string;
  active: boolean;
  memo?: string;
}): Promise<PlatformSubscriptionRow> {
  return await platformPost("/platform-subscriptions/set-active", params);
}

export async function subscriptionTransferOwner(params: {
  subscriptionId: string;
  userId: string;
}): Promise<PlatformSubscriptionRow> {
  return await platformPost("/platform-subscriptions/transfer-owner", params);
}

export type PlatformSubscriptionFilter = {
  search?: string;
  tier?: string;
  active?: boolean;
  limit?: number;
  offset?: number;
};

export async function listSubscriptions(
  filter: PlatformSubscriptionFilter,
): Promise<PlatformPagedResult<PlatformSubscriptionRow>> {
  return await platformPost("/platform-subscriptions/list", filter);
}

export type PlatformSubscriptionCreate = {
  teamId: string;
  tier: string;
  licenses: number;
  startDate?: string;
  endDate?: string;
  isTrial?: boolean;
  isOneTime?: boolean;
  stripeCustomerId?: string;
  stripeSubscriptionItemId?: string;
  billingAdminIds?: string[];
  memo?: string;
};

export async function subscriptionCreate(
  params: PlatformSubscriptionCreate,
): Promise<PlatformSubscriptionRow> {
  return await platformPost("/platform-subscriptions/create", params);
}
